import { useAuthStore } from '@/features/auth';
import { useConversationMembers } from '@/features/conversation';
import { useMessageDeletePin } from '@/features/messages/queries/use-message-pin-delete';
import { useMessagePinned } from '@/features/messages/queries/use-message-pinned';
import { chatEE } from '@/layout';
import { cn } from '@/shared/utilities';
import { Delete, ExpandMore, PushPin } from '@mui/icons-material';
import { Box, Button, ClickAwayListener, Collapse, IconButton, Paper, Popper, Stack, Typography } from '@mui/material';
import { memo, useCallback, useRef, useState } from 'react';
import { MessageListPinnedItem } from './message-list-pinned-item';
import { PinnedMessagesPopup } from './message-list-pinned-popup';

export interface MessageListPinnedProps {
  conversationId: string;
  className?: string;
  maxDisplay?: number;
}

export const MessageListPinned = memo<MessageListPinnedProps>(({ conversationId, className, maxDisplay = 3 }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const anchorRef = useRef<HTMLElement>(null);

  const userId = useAuthStore((s) => s.user?.user_id);
  const { data, isLoading, error, refetch } = useMessagePinned(conversationId, 20);
  const { data: members } = useConversationMembers(conversationId);
  const { xoa } = useMessageDeletePin(conversationId);
  const pinnedMessages = data?.pages?.flatMap((page) => page.pinnedMessages) || [];

  const handlePinnedMsgClick = useCallback(
    (messageId: string) => {
      chatEE.emit('zoom-to-message', { conversationId: conversationId!, messageId: messageId });
      setDropdownOpen(false);
    },
    [conversationId]
  );

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  const handleClickAway = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const handleViewAll = useCallback(() => {
    setPopupOpen(true);
    setDropdownOpen(false);
  }, []);

  const handlePopupClose = useCallback(() => {
    setPopupOpen(false);
  }, []);

  // Don't render if no pinned messages or loading/error
  if (!pinnedMessages.length || isLoading || error) {
    return null;
  }

  const mainMessage = pinnedMessages[0];
  const hasMultiple = pinnedMessages.length > 1;

  const handleDeletePin = async (messageId: string, bucketId: string) => {
    if (mainMessage) {
      await xoa(messageId, bucketId);
      await refetch();
    }
  };

  return (
    <div id='message-pinned' className={cn('mex:border-gray-200 mex:border-b mex:bg-gray-50', className)}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box className={cn('mex:relative', className)}>
          <Box
            ref={anchorRef}
            className='mex:flex mex:min-w-0 mex:flex-1 mex:cursor-pointer mex:items-center mex:gap-2'
            onClick={hasMultiple ? undefined : () => handlePinnedMsgClick(mainMessage.messageId)}
          >
            <Button
              variant='text'
              size='small'
              className='mex:min-w-0 mex:flex-1 mex:justify-start mex:p-0'
              endIcon={
                hasMultiple ? (
                  <ExpandMore
                    className={cn('mex:transition-transform mex:duration-200', dropdownOpen && 'mex:rotate-180')}
                  />
                ) : undefined
              }
              onClick={hasMultiple ? handleDropdownToggle : () => handlePinnedMsgClick(mainMessage.messageId)}
            >
              <Stack direction='row' alignItems='center' gap={1} className='mex:min-w-0 mex:flex-1'>
                <PushPin className='mex:text-orange-500' fontSize='small' />
                <Stack direction='row' alignItems='center' gap={1} className='mex:min-w-0'>
                  <Typography
                    variant='body2'
                    className='mex:text-left'
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                    noWrap
                  >
                    {(members || []).find((u) => u.userId === mainMessage.senderId)?.user?.fullName}:{' '}
                    {mainMessage.content}
                  </Typography>
                  {hasMultiple && (
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      và {pinnedMessages.length - 1} tin nhắn khác được ghim
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Button>
            {mainMessage.pinnedBy === userId && (
              <IconButton onClick={() => handleDeletePin(mainMessage.messageId, mainMessage.bucketId)} size='small'>
                <Delete fontSize='small' className='mex:text-red-500' />
              </IconButton>
            )}
          </Box>
          <Popper
            container={anchorRef.current}
            open={dropdownOpen}
            anchorEl={anchorRef.current}
            placement='bottom-end'
            className='mex:w-full'
            style={{ zIndex: 1300 }}
          >
            <Paper
              elevation={8}
              sx={{
                maxHeight: 400,
                overflow: 'hidden'
              }}
            >
              <Collapse in={dropdownOpen}>
                <Box>
                  {pinnedMessages.slice(0, maxDisplay).map((message) => (
                    <MessageListPinnedItem
                      curUserId={userId || ''}
                      key={message.messageId}
                      message={message}
                      onClick={handlePinnedMsgClick}
                      onDelete={handleDeletePin}
                      showPinnedInfo={false}
                    />
                  ))}

                  {pinnedMessages.length > maxDisplay && (
                    <Box className='mex:border-gray-100 mex:border-t mex:p-3'>
                      <Button
                        onClick={handleViewAll}
                        variant='text'
                        size='small'
                        fullWidth
                        sx={{
                          color: 'primary.main',
                          fontWeight: 500
                        }}
                      >
                        Xem tất cả ({pinnedMessages.length} tin nhắn)
                      </Button>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      <PinnedMessagesPopup
        open={popupOpen}
        onClose={handlePopupClose}
        conversationId={conversationId}
        onMessageClick={handlePinnedMsgClick}
      />
    </div>
  );
});

MessageListPinned.displayName = 'MessagePinned';
